import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUpload, FiDownload, FiCheck, FiX } from 'react-icons/fi';
import apiService from '../../services/api';
import Notification from '../common/Notification';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const ImportCard = styled(motion.div)`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.isDragOver ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 0;
  padding: 2rem;
  text-align: center;
  background: ${props => props.isDragOver ? 'rgba(216, 184, 137, 0.1)' : 'var(--background-primary)'};
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    border-color: var(--primary-color);
    background: rgba(216, 184, 137, 0.1);
  }
`;

const UploadIcon = styled.div`
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const UploadText = styled.p`
  color: var(--text-primary);
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
`;

const UploadSubtext = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
`;

const FileInput = styled.input`
  display: none;
`;


const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background-color: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-tertiary);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  margin-top: 1.5rem;
`;

const PreviewTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const PreviewTable = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: var(--background-tertiary);
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
`;

const TableRow = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ResultsSection = styled.div`
  margin-top: 1.5rem;
`;

const ResultsTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ResultCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  text-align: center;
`;

const ResultNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || 'var(--text-primary)'};
  margin-bottom: 0.5rem;
`;

const ResultLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const ErrorList = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 0;
  padding: 1rem;
  margin-top: 1rem;
`;

const ErrorTitle = styled.h4`
  color: var(--error-color);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const ErrorItem = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const TemplateSection = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const TemplateTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const TemplateText = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
`;

function BulkStudentsImportModal({ isOpen, onClose }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [notification, setNotification] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [filePath, setFilePath] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setImportResults(null);
    } else {
      setNotification({
        type: 'error',
        message: 'Please select a valid CSV file'
      });
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setNotification({
        type: 'error',
        message: 'Please select a CSV file to import'
      });
      return;
    }

    setIsImporting(true);
    try {
      const results = await apiService.importCSV(selectedFile);
      
      if (results.requiresConfirmation) {
        setPreviewData(results.preview);
        setFilePath(results.preview.filePath || 'temp');
        setNotification({
          type: 'info',
          message: results.message
        });
      } else {
        setImportResults(results.results);
        setNotification({
          type: 'success',
          message: results.message
        });
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Import failed'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!filePath || !previewData) {
      setNotification({
        type: 'error',
        message: 'Missing import data'
      });
      return;
    }

    setIsImporting(true);
    try {
      const results = await apiService.confirmCSVImport(filePath, previewData.totalRecords);
      setImportResults(results.results);
      setPreviewData(null);
      setFilePath(null);
      setNotification({
        type: 'success',
        message: results.message
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Import confirmation failed'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setPreviewData(null);
    setFilePath(null);
    setNotification({
      type: 'info',
      message: 'Import cancelled'
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      await apiService.downloadCSVTemplate();
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to download template'
      });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResults(null);
    setPreviewData(null);
    setFilePath(null);
    setNotification(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>Bulk Students Import</ModalTitle>
          <CloseButton onClick={handleClose}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}

        <TemplateSection>
          <TemplateTitle>Download Template</TemplateTitle>
          <TemplateText>
            Download our CSV template to ensure your file has the correct format.
          </TemplateText>
          <Button className="secondary" onClick={handleDownloadTemplate}>
            <FiDownload size={16} />
            Download Template
          </Button>
        </TemplateSection>

        <ImportCard>
          <UploadArea
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
          >
            <UploadIcon>
              <FiUpload size={48} />
            </UploadIcon>
            <UploadText>
              {selectedFile ? selectedFile.name : 'Drag & drop your CSV file here'}
            </UploadText>
            <UploadSubtext>
              or click to browse files
            </UploadSubtext>
            <FileInput
              id="file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
            />
          </UploadArea>

          {selectedFile && (
            <ActionButtons>
              <Button
                className="primary"
                onClick={handleImport}
                disabled={isImporting}
              >
                {isImporting ? (
                  <>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid transparent', 
                      borderTop: '2px solid white', 
                      borderRadius: 0, 
                      animation: 'spin 1s linear infinite' 
                    }} />
                    Importing...
                  </>
                ) : (
                  <>
                    <FiUpload size={16} />
                    Import Students
                  </>
                )}
              </Button>
            </ActionButtons>
          )}
        </ImportCard>

        {previewData && (
          <PreviewSection>
            <PreviewTitle>Import Preview</PreviewTitle>
            <PreviewTable>
              <TableHeader>
                Found {previewData.totalRecords} students to import
              </TableHeader>
              {previewData.previewRecords?.slice(0, 5).map((record, index) => (
                <TableRow key={index}>
                  {record['First Name']} {record['Last Name']} ({record['Email']})
                </TableRow>
              ))}
              {previewData.totalRecords > 5 && (
                <TableRow>... and {previewData.totalRecords - 5} more</TableRow>
              )}
            </PreviewTable>
            <ActionButtons>
              <Button
                className="primary"
                onClick={handleConfirmImport}
                disabled={isImporting}
              >
                <FiCheck size={16} />
                Confirm Import
              </Button>
              <Button
                className="secondary"
                onClick={handleCancelImport}
                disabled={isImporting}
              >
                Cancel
              </Button>
            </ActionButtons>
          </PreviewSection>
        )}

        {importResults && (
          <ResultsSection>
            <ResultsTitle>Import Results</ResultsTitle>
            <ResultsGrid>
              <ResultCard>
                <ResultNumber color="var(--primary-color)">
                  {importResults.successful}
                </ResultNumber>
                <ResultLabel>Successfully Imported</ResultLabel>
              </ResultCard>
              <ResultCard>
                <ResultNumber color="var(--error-color)">
                  {importResults.failed}
                </ResultNumber>
                <ResultLabel>Failed</ResultLabel>
              </ResultCard>
              <ResultCard>
                <ResultNumber color="var(--success-dark)">
                  {importResults.existingUsers}
                </ResultNumber>
                <ResultLabel>Existing Users Enrolled</ResultLabel>
              </ResultCard>
              {importResults.alreadyEnrolled > 0 && (
                <ResultCard>
                  <ResultNumber color="var(--text-muted)">
                    {importResults.alreadyEnrolled}
                  </ResultNumber>
                  <ResultLabel>Already Enrolled (Skipped)</ResultLabel>
                </ResultCard>
              )}
            </ResultsGrid>

            {importResults.errors && importResults.errors.length > 0 && (
              <ErrorList>
                <ErrorTitle>Errors:</ErrorTitle>
                {importResults.errors.map((error, index) => (
                  <ErrorItem key={index}>{error}</ErrorItem>
                ))}
              </ErrorList>
            )}
          </ResultsSection>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default BulkStudentsImportModal;
